import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatCard from '../../components/StatCard';
import OnboardingSummary from '../../components/OnboardingSummary';
import TimesheetDownload from '../../components/TimesheetDownload';
import DocumentsToReview from '../../components/DocumentsToReview';
import { issuesAPI, onboardingAPI, performanceAPI, taskRequestsAPI, tasksAPI } from '../../services/api';
import useRefreshOnFocus from '../../utils/useRefreshOnFocus';

export default function HRDashboard() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [openIssues, setOpenIssues] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [newEnquiries, setNewEnquiries] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    Promise.all([
      performanceAPI.overview(),
      tasksAPI.list(),
      issuesAPI.list({ issue_status: 'open' }),
      taskRequestsAPI.list(),
    ]).then(([ov, t, iss, reqs]) => {
      setOverview(ov.data);
      setTasks(t.data.slice(0, 8));
      setOpenIssues(iss.data.slice(0, 5));
      setPendingRequests(reqs.data.filter(r => r.status === 'pending').slice(0, 5));
    }).finally(() => setLoading(false));
    onboardingAPI.list({ stage: 'tutor_suggestion' })
      .then(({ data }) => setNewEnquiries(data.length)).catch(() => {});
  };

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useRefreshOnFocus(refresh);

  if (loading) return <div className="page-loading">Loading dashboard...</div>;

  const totalTasks = overview.reduce((a, e) => a + e.total_tasks, 0);
  const totalCompleted = overview.reduce((a, e) => a + e.completed, 0);
  const totalOverdue = overview.reduce((a, e) => a + e.overdue, 0);

  const statusColor = { pending: 'orange', in_progress: 'blue', completed: 'green', overdue: 'red', cancelled: 'gray' };
  const urgColor = { low: 'green', medium: 'blue', high: 'orange', critical: 'red' };

  return (
    <div className="page">
      <div className="page-header">
        <h2>HR Dashboard</h2>
        <Link to="/hr/tasks/assign" className="btn btn-primary">+ Assign Task</Link>
      </div>

      <TimesheetDownload presets />

      <div className="stats-grid">
        <StatCard title="New Enquiries" value={newEnquiries} icon="🎓" color="teal"
          onClick={() => navigate('/onboarding?stage=tutor_suggestion')} />
        <StatCard title="Total Team Tasks" value={totalTasks} icon="📋" color="blue" />
        <StatCard title="Completed" value={totalCompleted} icon="✅" color="green" />
        <StatCard title="Overdue" value={totalOverdue} icon="⏰" color="red" />
        <StatCard title="Open Issues" value={openIssues.length} icon="⚠" color="orange" />
        <StatCard title="Employees" value={overview.length} icon="👥" color="purple" />
      </div>

      <div className="dashboard-grid">
        <div className="card">
          <div className="card-header">
            <h3>Recent Tasks</h3>
            <Link to="/hr/tasks" className="link-sm">View all →</Link>
          </div>
          <table className="data-table">
            <thead><tr><th>Title</th><th>Employee</th><th>Status</th><th>Priority</th></tr></thead>
            <tbody>
              {tasks.map((t) => (
                <tr key={t.id}>
                  <td><Link to={`/hr/tasks/${t.id}`} className="table-link">{t.title}</Link></td>
                  <td>{t.assigned_to?.full_name || '—'}</td>
                  <td><span className={`badge badge-${statusColor[t.status]}`}>{t.status.replace('_', ' ')}</span></td>
                  <td><span className={`badge badge-${statusColor[t.priority] || 'blue'}`}>{t.priority}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card">
          <div className="card-header">
            <h3>Open Issues</h3>
            <Link to="/hr/issues" className="link-sm">View all →</Link>
          </div>
          {openIssues.length === 0 ? (
            <p className="text-muted" style={{ padding: '12px 20px' }}>No open issues.</p>
          ) : (
            openIssues.map((iss) => (
              <div key={iss.id} className="issue-item">
                <span className={`badge badge-${urgColor[iss.urgency]}`}>{iss.urgency}</span>
                <span className="ml-2">{iss.title}</span>
                <span className="text-muted ml-2">by {iss.raised_by?.full_name}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {pendingRequests.length > 0 && (
        <div className="card mt-6">
          <div className="card-header">
            <h3>Pending Employee Requests</h3>
            <Link to="/hr/tasks" className="link-sm">View tasks →</Link>
          </div>
          {pendingRequests.map((r) => (
            <div key={r.id} className="issue-item">
              <span className={`badge badge-${r.type === 'review' ? 'blue' : r.type === 'meeting' ? 'purple' : 'orange'}`}>{r.type}</span>
              <span className="ml-2">Task #{r.task_id}</span>
              {r.notes && <span className="text-muted ml-2">— {r.notes.substring(0, 60)}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="card mt-6">
        <div className="card-header">
          <h3>Team Performance Summary</h3>
          <Link to="/hr/employees" className="link-sm">View details →</Link>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th><th>Department</th><th>Total</th><th>Completed</th><th>Overdue</th><th>Efficiency</th>
            </tr>
          </thead>
          <tbody>
            {overview.map((e) => (
              <tr key={e.user.id}>
                <td><Link to={`/hr/employees/${e.user.id}`} className="table-link">{e.user.full_name}</Link></td>
                <td>{e.user.department || '—'}</td>
                <td>{e.total_tasks}</td>
                <td>{e.completed}</td>
                <td className={e.overdue > 0 ? 'text-danger' : ''}>{e.overdue}</td>
                <td>{e.efficiency_percent != null ? `${e.efficiency_percent}%` : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <DocumentsToReview />

      <OnboardingSummary />
    </div>
  );
}
